/* Judge.me Public Widget API. This token permits public widget reads only. */
(function () {
  'use strict';
  const PUBLIC_WIDGET_TOKEN = 'A36SeUdowQWu1x9iFGMyGRu4bBk';
  const cache = new Map();
  const text = (node, selector) => (node.querySelector(selector)?.textContent || '').trim();
  function path(url) {
    try { const parsed = new URL(url, location.origin); return parsed.origin === location.origin ? parsed.pathname : ''; }
    catch (_) { return ''; }
  }
  function safeMedia(url) {
    try { const parsed = new URL(url); return parsed.protocol === 'https:' && /(^|\.)(judge\.me|judgeme\.s3\.amazonaws\.com|shopify\.com)$/.test(parsed.hostname) ? parsed.href : ''; }
    catch (_) { return ''; }
  }
  async function readSource(source) {
    if (cache.has(source.id)) return cache.get(source.id);
    const work = (async function () {
      const cards = [];
      const pages = new Set();
      let page = 1;
      while (page && !pages.has(page) && pages.size < 100) {
        pages.add(page);
        const url = new URL('https://judge.me/api/v1/widgets/product_review');
        url.search = new URLSearchParams({api_token: PUBLIC_WIDGET_TOKEN, shop_domain: 'saytouche.myshopify.com', external_id: String(source.id), per_page: '30', page: String(page)});
        const response = await fetch(url, {signal: AbortSignal.timeout(15000), credentials: 'omit'});
        if (!response.ok) throw new Error('Review service unavailable');
        const payload = await response.json();
        if (String(payload.product_external_id) !== String(source.id) || typeof payload.widget !== 'string') throw new Error('Unexpected review product');
        const doc = new DOMParser().parseFromString(payload.widget, 'text/html');
        doc.querySelectorAll('.jdgm-rev[data-review-id]').forEach(function (node) {
          // Grouped widgets include other products. Do not relabel those as this component.
          const origin = node.getAttribute('data-product-url');
          const title = node.getAttribute('data-product-title');
          if (origin ? path(origin) !== path(source.url) : title && title !== source.title) return;
          const rating = Number(node.querySelector('.jdgm-rev__rating')?.getAttribute('data-score'));
          if (!Number.isInteger(rating) || rating < 1 || rating > 5) return;
          cards.push({
            id: node.getAttribute('data-review-id'), source: source,
            author: text(node, '.jdgm-rev__author'), title: text(node, '.jdgm-rev__title'),
            body: Array.from(node.querySelectorAll('.jdgm-rev__body')).map(body => Array.from(body.childNodes).map(p => p.textContent).join('\n')).join('\n').trim(),
            rating: rating, date: node.querySelector('time')?.getAttribute('datetime') || '',
            verified: node.getAttribute('data-verified-buyer') === 'true',
            incentive: Array.from(node.querySelectorAll('[data-badge-type]')).some(b => /earned|incentiv|reward/.test(b.getAttribute('data-badge-type'))),
            reply: text(node, '.jdgm-rev__reply-content'),
            pictures: Array.from(node.querySelectorAll('.jdgm-rev__pics img')).map(img => safeMedia(img.getAttribute('data-src') || img.getAttribute('src'))).filter(Boolean)
          });
        });
        const next = Array.from(doc.querySelectorAll('.jdgm-paginate [data-page]')).map(a => Number(a.getAttribute('data-page'))).filter(n => Number.isInteger(n) && n > page).sort((a,b) => a-b)[0];
        page = next || 0;
      }
      if (page) throw new Error('Incomplete review pagination');
      return cards;
    })();
    cache.set(source.id, work);
    return work;
  }
  function element(tag, className, content) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content) node.textContent = content;
    return node;
  }
  function renderReview(review) {
    const card = element('article', 'set-review-card');
    card.dataset.reviewId = review.id;
    card.dataset.sourceProduct = String(review.source.id);
    const meta = element('div', 'set-review-meta');
    const stars = element('span', 'set-review-stars', '★'.repeat(review.rating) + '☆'.repeat(5-review.rating));
    stars.setAttribute('aria-label', review.rating + ' out of 5 stars');
    meta.append(stars, element('span', '', review.author));
    if (review.verified) meta.append(element('span', '', 'Verified buyer'));
    if (review.date && !Number.isNaN(Date.parse(review.date))) {
      const date = element('time', '', new Date(review.date).toLocaleDateString('en-AU', {day:'numeric',month:'short',year:'numeric'}));
      date.dateTime = review.date;
      meta.append(date);
    }
    const source = element('a', 'set-review-source', 'Review for: ' + review.source.title);
    source.href = review.source.url + '#judgeme_product_reviews';
    card.append(meta, source);
    if (review.title) card.append(element('h3', '', review.title));
    if (review.body) card.append(element('p', '', review.body));
    review.pictures.forEach(url => {const image = element('img', 'set-review-photo');image.src=url;image.alt='Customer photo of '+review.source.title;image.loading='lazy';card.append(image);});
    if (review.incentive) card.append(element('p', 'set-review-notice', 'An incentive was offered for this review.'));
    if (review.reply) card.append(element('p', 'set-review-notice', 'Mynt Lab replied: ' + review.reply));
    return card;
  }
  async function load(root) {
    const status = root.querySelector('[data-component-review-status]');
    const list = root.querySelector('[data-component-review-list]');
    const more = root.querySelector('[data-component-review-more]');
    try {
      const sources = JSON.parse(root.querySelector('[data-component-review-sources]').textContent).filter(s => s.id && s.title && path(s.url));
      if (!sources.length) throw new Error('No review sources');
      const outcomes = await Promise.allSettled(sources.map(readSource));
      const failed = outcomes.some(r => r.status === 'rejected');
      const reviews = Array.from(new Map(outcomes.filter(r => r.status === 'fulfilled').flatMap(r => r.value).map(r => [r.id, r])).values()).sort((a,b) => (Date.parse(b.date)||0)-(Date.parse(a.date)||0));
      root.dataset.reviewCount = String(reviews.length);
      root.dataset.reviewAvg = reviews.length ? (reviews.reduce(function (s, r) { return s + r.rating; }, 0) / reviews.length).toFixed(2) : '0';
      root.dataset.reviewLoad = failed ? 'partial' : 'complete';
      status.textContent = failed ? 'Some reviews could not load. You can also read reviews on each product page below.' : reviews.length ? reviews.length + ' reviews of the included products' : 'No reviews of these individual products yet.';
      let shown = 0;
      function showMore() {
        reviews.slice(shown, shown+5).forEach(review => list.append(renderReview(review)));
        shown += 5;
        more.hidden = shown >= reviews.length;
      }
      more.addEventListener('click', showMore);
      showMore();
    } catch (_) {
      root.dataset.reviewLoad = 'failed';
      status.textContent = 'Read customer reviews on the individual product pages below.';
    }
  }
  document.querySelectorAll('[data-set-component-reviews]').forEach(function(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    // Start early so review counts are ready by the time the shopper scrolls down.
    load(root);
  });
})();
